/**
 * Dispatch bridge factory — Story 4.2/4.3/4.4 carry-over, extracted in
 * Story 4.6 wrap-up.
 *
 * The god-context body has many `dispatch({ type: ACTIONS.X, payload })`
 * call sites that target action types now owned by other slices
 * (Session: SET_BACKEND_SESSION, etc.; Messages: ADD_MESSAGE, etc.;
 * Results: UPDATE_SESSION_DATA + 12 SET_X). Without this bridge those
 * dispatches would silently no-op — the slim god-context reducer
 * doesn't recognise them.
 *
 * Returns a dispatch function that forwards moved-action dispatches to
 * the right slice's action-creator and falls through to `innerDispatch`
 * for everything else (workflow-state, UI, isRevising, reset).
 */
import { ACTIONS } from './WorkflowMachineContext';

export function createDispatchBridge({
  innerDispatch,
  workflowSession,
  workflowMessages,
  workflowResults,
}) {
  return function bridgeDispatch(action) {
    if (!action || !action.type) return innerDispatch(action);
    switch (action.type) {
      // Messages slice (Story 4.3)
      case ACTIONS.ADD_MESSAGE:
        workflowMessages.addMessage(action.payload);
        return;
      case ACTIONS.UPDATE_MESSAGE:
        workflowMessages.updateMessage(action.payload.id, action.payload.updates);
        return;
      case ACTIONS.CLEAR_MESSAGES:
        workflowMessages.clearMessages();
        return;

      // Session slice (Story 4.2)
      case ACTIONS.SET_BACKEND_SESSION:
        workflowSession.setBackendSession(action.payload);
        return;
      case ACTIONS.UPDATE_BACKEND_SESSION:
        workflowSession.updateBackendSession(action.payload);
        return;
      case ACTIONS.SET_BACKEND_SESSIONS:
      case ACTIONS.SET_LOADING_BACKEND_SESSIONS:
      case ACTIONS.SET_BACKEND_SESSIONS_ERROR:
      case ACTIONS.SET_TENANT_ID:
        // No call sites in the god-context body use these directly today
        // (they were only dispatched by the moved action-creators). Left
        // explicit to make the bridge contract obvious for future readers.
        return;

      // Results slice (Story 4.4)
      case ACTIONS.UPDATE_SESSION_DATA:
        workflowResults.updateSessionData(action.payload);
        return;
      case ACTIONS.SET_DOCUMENTS:
        workflowResults.setDocuments(action.payload);
        return;
      case ACTIONS.SET_ITEM_DEFINITION:
        workflowResults.setItemDefinition(action.payload);
        return;
      case ACTIONS.SET_ASSETS:
        workflowResults.setAssets(action.payload);
        return;
      case ACTIONS.SET_THREATS:
        workflowResults.setThreats(action.payload);
        return;
      case ACTIONS.SET_DAMAGE_SCENARIOS:
        workflowResults.setDamageScenarios(action.payload);
        return;
      case ACTIONS.SET_ATTACK_PATHS:
        workflowResults.setAttackPaths(action.payload);
        return;
      case ACTIONS.SET_RISKS:
        workflowResults.setRisks(action.payload);
        return;
      case ACTIONS.SET_CYBERSECURITY_GOALS:
        workflowResults.setCybersecurityGoals(action.payload);
        return;
      case ACTIONS.SET_CYBERSECURITY_CLAIMS:
        workflowResults.setCybersecurityClaims(action.payload);
        return;
      case ACTIONS.SET_CYBERSECURITY_CONTROLS:
        workflowResults.setCybersecurityControls(action.payload);
        return;
      case ACTIONS.SET_REQUIREMENTS:
        workflowResults.setRequirements(action.payload);
        return;
      case ACTIONS.SET_REPORT:
        workflowResults.setReport(action.payload);
        return;

      default:
        return innerDispatch(action);
    }
  };
}
